import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../lib/AuthContext'
import {
  getForYouFeed, getLatestFeed, getPendingCapsules, getPublicIncubatingCapsules,
  withdrawCapsule, signOut, getTopDepthUsers, getCivicEvents, getCivicCities
} from '../lib/supabase'
import { useToast } from '../lib/useToast'
import CapsuleCard, { PendingCapsuleCard } from '../components/CapsuleCard'
import Compose from '../components/Compose'
import NotificationButton from '../components/NotificationButton'
import DirectMessagesButton from '../components/DirectMessagesButton'
import CivicEventCard from '../components/CivicEventCard'
import './Feed.css'

const TOPICS = ['All', 'Philosophy', 'Economics', 'Politics', 'Science', 'Culture', 'Technology']

const DEBATE_TOPICS = [
  {
    domain: 'Philosophy',
    icon: '◎',
    topics: [
      {
        id: 'free-will',
        title: 'Free Will vs. Determinism',
        question: 'Is genuine free will compatible with a deterministic universe — and does it matter either way?'
      },
      {
        id: 'consciousness',
        title: 'The Hard Problem of Consciousness',
        question: 'Why is there subjective experience at all? Can physicalism ever fully explain qualia?'
      },
      {
        id: 'moral-realism',
        title: 'Moral Realism vs. Relativism',
        question: 'Are there objective moral truths, or is morality always relative to culture, perspective, or preference?'
      },
      {
        id: 'identity',
        title: 'Personal Identity Over Time',
        question: 'What makes you the same person you were ten years ago? Is continuity of self an illusion?'
      },
    ]
  },
  {
    domain: 'Economics',
    icon: '◈',
    topics: [
      {
        id: 'ubi',
        title: 'Universal Basic Income',
        question: 'Would a guaranteed income floor liberate human potential — or hollow out the meaning of work?'
      },
      {
        id: 'inequality',
        title: 'Wealth Inequality & Redistribution',
        question: 'Is extreme wealth concentration a moral failure, an economic inefficiency, or simply the inevitable outcome of markets?'
      },
      {
        id: 'degrowth',
        title: 'Degrowth vs. Green Growth',
        question: 'Can we decouple GDP growth from environmental destruction — or does sustainability require shrinking the economy itself?'
      },
      {
        id: 'capitalism',
        title: "Capitalism's Future",
        question: 'Can capitalism reform itself to address climate and inequality, or does it require fundamental systemic alternatives?'
      },
    ]
  },
  {
    domain: 'Politics',
    icon: '◇',
    topics: [
      {
        id: 'democracy',
        title: 'Crisis of Liberal Democracy',
        question: 'Is liberal democracy structurally ill-equipped for 21st century challenges, or is it still the least-bad system?'
      },
      {
        id: 'sovereignty',
        title: 'National Sovereignty vs. Global Governance',
        question: 'Should nations cede power to international institutions to tackle global problems — and who decides when?'
      },
      {
        id: 'immigration',
        title: 'Open Borders vs. Immigration Control',
        question: 'Do states have a moral right to restrict migration, or is freedom of movement a fundamental human right?'
      },
      {
        id: 'state-markets',
        title: 'The Role of the State in Markets',
        question: 'Where should the line between market efficiency and state intervention be drawn in a modern economy?'
      },
    ]
  },
  {
    domain: 'Science & Ethics',
    icon: '⬡',
    topics: [
      {
        id: 'ai-risk',
        title: 'AI Alignment & Existential Risk',
        question: 'How seriously should we take the possibility that advanced AI poses an existential risk — and how do we govern it?'
      },
      {
        id: 'ea',
        title: 'Effective Altruism',
        question: 'Is EA the most rigorous framework for doing good, or does it reduce ethics to a utilitarian calculation that misses what matters most?'
      },
      {
        id: 'gene-editing',
        title: 'Human Genetic Enhancement',
        question: 'Should we use gene editing to enhance human capabilities beyond treating disease — and where is the ethical line?'
      },
      {
        id: 'longtermism',
        title: 'Longtermism',
        question: 'Should we dramatically weight the interests of future generations above present ones when making moral and policy decisions?'
      },
    ]
  },
  {
    domain: 'Culture',
    icon: '◉',
    topics: [
      {
        id: 'cancel-culture',
        title: 'Cancel Culture & Social Accountability',
        question: 'Is public accountability via social media a legitimate tool of justice, or has it become a form of mob punishment?'
      },
      {
        id: 'religion',
        title: "Religion's Role in Modern Society",
        question: 'Does organized religion still serve essential functions in secular societies, or has it become an obstacle to progress?'
      },
      {
        id: 'tech-alienation',
        title: 'Technology & Human Alienation',
        question: 'Are smartphones and social media fundamentally rewiring human cognition and social bonds in ways we cannot undo?'
      },
      {
        id: 'meritocracy',
        title: 'The Myth of Meritocracy',
        question: 'Does meritocracy describe the world as it is, justify the world as it should be — or is it largely a fiction that serves the powerful?'
      },
    ]
  },
]

export default function Feed() {
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const { toast, showToast } = useToast()

  const [searchQuery, setSearchQuery] = useState('')
  const [topDepthUsers, setTopDepthUsers] = useState([])

  const handleSearch = (e) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`)
    }
  }

  const [searchParams] = useSearchParams()

  const [mainTab, setMainTab] = useState(() => {
    // Allow linking directly to civic tab via ?civic_tab=true
    return searchParams.get('civic_tab') === 'true' ? 'civic' : 'discussions'
  }) // 'discussions' | 'debates' | 'civic'
  const [feedTab, setFeedTab] = useState('foryou') // 'foryou' | 'latest' | 'incubating'
  const [selectedTopic, setSelectedTopic] = useState('All')

  const [forYouPosts, setForYouPosts] = useState([])
  const [latestPosts, setLatestPosts] = useState([])
  const [pendingPosts, setPendingPosts] = useState([])
  const [publicIncubatingPosts, setPublicIncubatingPosts] = useState([])

  const [loading, setLoading] = useState(true)

  // Civic feed state
  const [civicEvents, setCivicEvents] = useState([])
  const [civicCities, setCivicCities] = useState([])
  const [civicLoading, setCivicLoading] = useState(false)
  const [civicCityFilter, setCivicCityFilter] = useState('All')
  const [civicTopicFilter, setCivicTopicFilter] = useState('All')

  // Withdraw modal
  const [withdrawTarget, setWithdrawTarget] = useState(null)

  // Compose prefill (from debates tab)
  const [composePrefill, setComposePrefill] = useState('')

  useEffect(() => {
    if (!user) { navigate('/'); return }
    loadAll()
    loadCivicFeed()
  }, [user]) // eslint-disable-line

  useEffect(() => {
    if (mainTab === 'civic') loadCivicFeed()
  }, [civicCityFilter, civicTopicFilter]) // eslint-disable-line

  const loadCivicFeed = async () => {
    setCivicLoading(true)
    try {
      const [events, cities] = await Promise.all([
        getCivicEvents({
          city: civicCityFilter !== 'All' ? civicCityFilter : undefined,
          topic: civicTopicFilter !== 'All' ? civicTopicFilter : undefined,
          limit: 30
        }),
        getCivicCities()
      ])
      setCivicEvents(events)
      setCivicCities(cities)
    } catch (e) {
      showToast('Error loading civic feed: ' + e.message, 'error')
    } finally {
      setCivicLoading(false)
    }
  }

  const loadAll = async () => {
    setLoading(true)
    try {
      const [forYou, latest, pending, incubating, topUsers] = await Promise.all([
        getForYouFeed(user.id, profile?.interests || [], 20, 0),
        getLatestFeed(20, 0),
        getPendingCapsules(user.id),
        getPublicIncubatingCapsules(30),
        getTopDepthUsers(8),
      ])
      setForYouPosts(forYou)
      setLatestPosts(latest)
      setPendingPosts(pending)
      setPublicIncubatingPosts(incubating)
      setTopDepthUsers(topUsers)
    } catch (e) {
      showToast('Error loading feed: ' + e.message, 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleNewCapsule = (capsule) => {
    setPendingPosts(prev => [capsule, ...prev])
    setPublicIncubatingPosts(prev => [capsule, ...prev])
    setFeedTab('incubating')
    showToast('✓ Capsule sealed. Entering incubation workspace for guided reflection.')
    navigate(`/workspace/${capsule.id}`)
  }

  const handleWithdraw = (capsule) => {
    setWithdrawTarget(capsule)
  }

  const confirmWithdraw = async () => {
    if (!withdrawTarget) return
    try {
      await withdrawCapsule(withdrawTarget.id, user.id)
      setPendingPosts(prev => prev.filter(p => p.id !== withdrawTarget.id))
      setPublicIncubatingPosts(prev => prev.filter(p => p.id !== withdrawTarget.id))
      showToast('Capsule withdrawn and deleted.')
    } catch (e) {
      showToast('Could not withdraw: ' + e.message, 'error')
    } finally {
      setWithdrawTarget(null)
    }
  }

  const openDiscussFromDebate = (topic) => {
    setMainTab('discussions')
    setComposePrefill(`On "${topic.title}": `)
    showToast('Switched to Discussions — compose your capsule below.')
  }

  const openDebateFromCivic = (civicEvent) => {
    setMainTab('discussions')
    setComposePrefill(`On the "${civicEvent.title}" civic matter in ${civicEvent.city}: `)
    showToast('Compose your take on this civic issue.')
  }

  const handleSignOut = async () => {
    await signOut()
    navigate('/')
  }

  const filteredForYou = selectedTopic === 'All'
    ? forYouPosts
    : forYouPosts.filter(p => p.tags?.includes(selectedTopic))

  const filteredLatest = selectedTopic === 'All'
    ? latestPosts
    : latestPosts.filter(p => p.tags?.includes(selectedTopic))

  const incubatingById = new Map()
  publicIncubatingPosts.forEach(post => incubatingById.set(post.id, post))
  pendingPosts.forEach(post => incubatingById.set(post.id, post))
  const incubatingPosts = Array.from(incubatingById.values()).sort(
    (a, b) => new Date(a.publishes_at) - new Date(b.publishes_at)
  )

  const replyGuaranteePosts = filteredLatest
    .filter(post => (post.profiles?.capsule_count || 0) <= 1 && (post.response_count || 0) === 0)
    .slice(0, 3)

  const getInitials = (name) => name?.split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase() || '?'

  return (
    <div className="feed-page">
      {/* Header */}
      <header className="feed-header">
        <div className="feed-logo">
          Agora
          <span className="feed-logo-beta">Beta</span>
        </div>
        <nav className="feed-nav">
          <button
            className={`feed-nav-btn ${mainTab === 'discussions' ? 'active' : ''}`}
            onClick={() => setMainTab('discussions')}
          >
            Discussions
          </button>
          <button
            className={`feed-nav-btn ${mainTab === 'debates' ? 'active' : ''}`}
            onClick={() => setMainTab('debates')}
          >
            Big Debates
          </button>
          <button
            className={`feed-nav-btn feed-nav-btn--civic ${mainTab === 'civic' ? 'active' : ''}`}
            onClick={() => { setMainTab('civic'); loadCivicFeed() }}
          >
            Civic Intel
          </button>
        </nav>

        {/* Search bar */}
        <form className="feed-search-form" onSubmit={handleSearch}>
          <span className="feed-search-icon">⌕</span>
          <input
            className="feed-search-input"
            placeholder="Search topics, ideas, people…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              type="button"
              className="feed-search-clear"
              onClick={() => setSearchQuery('')}
            >×</button>
          )}
        </form>

        <div className="feed-header-right">
          <DirectMessagesButton />
          <NotificationButton userId={user?.id} />
          <button className="feed-profile-btn" onClick={() => navigate('/profile')}>
            <div
              className="feed-avatar"
              style={{ background: profile?.avatar_color || '#152550' }}
            >
              {profile?.avatar_url ? (
                <img src={profile.avatar_url} alt="" className="feed-avatar-image" />
              ) : (
                getInitials(profile?.display_name)
              )}
            </div>
            <span>{profile?.username ? `@${profile.username}` : (profile?.display_name || user?.email)}</span>
          </button>
          <button className="feed-signout" onClick={handleSignOut}>Sign out</button>
        </div>
      </header>

      {/* Main */}
      <div className="feed-layout">
        {/* Sidebar */}
        <aside className="feed-sidebar">
          <div className="sidebar-section">
            <div className="sidebar-label">Topics</div>
            {TOPICS.map(topic => (
              <button
                key={topic}
                className={`sidebar-topic ${selectedTopic === topic ? 'active' : ''}`}
                onClick={() => setSelectedTopic(topic)}
              >
                {topic}
              </button>
            ))}
          </div>

          {profile && (
            <div className="sidebar-section">
              <div className="sidebar-label">Your Depth</div>
              <div className="sidebar-depth">
                <div className="sidebar-depth-score-row">
                  <span className="sidebar-depth-number">⬡ {Math.round(profile.depth_score || 0)}</span>
                  <span className="sidebar-depth-label">{
                    (profile.depth_score || 0) >= 80 ? 'High Depth' :
                    (profile.depth_score || 0) >= 50 ? 'Mid Depth' :
                    (profile.depth_score || 0) >= 20 ? 'Growing' : 'New'
                  }</span>
                </div>
                <div className="sidebar-depth-bar-wrap">
                  <div
                    className="sidebar-depth-bar"
                    style={{ width: `${Math.min(profile.depth_score || 0, 100)}%` }}
                  />
                </div>
                <div className="depth-row" style={{ marginTop: '0.5rem' }}>
                  <span>Capsules</span>
                  <span className="depth-val">{profile.capsule_count || 0}</span>
                </div>
                <div className="depth-row">
                  <span>Responses</span>
                  <span className="depth-val">{profile.response_count || 0}</span>
                </div>
                <div className="depth-note">Read time · response depth · bridge impact</div>
              </div>
            </div>
          )}

          {topDepthUsers.length > 0 && (
            <div className="sidebar-section">
              <div className="sidebar-label">Depth Leaders</div>
              <div className="sidebar-leaderboard">
                {topDepthUsers.map((u, i) => {
                  const isMe = user?.id === u.id
                  return (
                    <div
                      key={u.id}
                      className={`sidebar-leader-row ${isMe ? 'sidebar-leader-row--me' : ''}`}
                      onClick={() => navigate(`/user/${u.username}`)}
                      title={`@${u.username}`}
                    >
                      <span className="sidebar-leader-rank">{i + 1}</span>
                      <div
                        className="sidebar-leader-avatar"
                        style={{ background: u.avatar_color || '#1D4ED8' }}
                      >
                        {(u.display_name || u.username || '?')[0].toUpperCase()}
                      </div>
                      <span className="sidebar-leader-name">
                        {u.display_name || u.username}
                        {isMe && <span className="sidebar-leader-you"> you</span>}
                      </span>
                      <span className="sidebar-leader-score">⬡ {Math.round(u.depth_score)}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </aside>

        {/* Feed */}
        <div className="feed-content">

          {/* ── DISCUSSIONS ── */}
          {mainTab === 'discussions' && (
            <>
              <Compose
                onSubmit={handleNewCapsule}
                prefillContent={composePrefill}
                key={composePrefill}
              />

              {replyGuaranteePosts.length > 0 && (
                <div className="reply-guarantee">
                  <div className="reply-guarantee-title">Reply Guarantee Queue (new voices)</div>
                  <div className="reply-guarantee-list">
                    {replyGuaranteePosts.map(post => (
                      <button
                        key={post.id}
                        className="reply-guarantee-item"
                        onClick={() => navigate(`/capsule/${post.id}?view=responses`)}
                      >
                        <span className="reply-guarantee-author">@{post.profiles?.username || 'member'}</span>
                        <span className="reply-guarantee-snippet">{post.content.slice(0, 110)}{post.content.length > 110 ? '…' : ''}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="feed-tabs">
                <button
                  className={`feed-tab ${feedTab === 'foryou' ? 'active' : ''}`}
                  onClick={() => setFeedTab('foryou')}
                >
                  For You
                </button>
                <button
                  className={`feed-tab ${feedTab === 'latest' ? 'active' : ''}`}
                  onClick={() => setFeedTab('latest')}
                >
                  Latest
                </button>
                <button
                  className={`feed-tab ${feedTab === 'incubating' ? 'active' : ''}`}
                  onClick={() => setFeedTab('incubating')}
                >
                  In Incubation
                  {incubatingPosts.length > 0 && (
                    <span className="incubation-count">{incubatingPosts.length}</span>
                  )}
                </button>
              </div>

              {loading ? (
                <div className="feed-loading">
                  <span className="spinner" />
                </div>
              ) : (
                <>
                  {/* FOR YOU */}
                  {feedTab === 'foryou' && (
                    <>
                      <div className="algo-note">
                        ⟡ Ranked by <strong>bridging score</strong> first (cross-tribe support), then depth. Not by raw engagement.
                      </div>
                      {filteredForYou.length === 0 ? (
                        <EmptyState
                          title="Your feed is growing"
                          body="As more people join and post, capsules matched to your interests will appear here."
                        />
                      ) : (
                        filteredForYou.map(c => (
                          <CapsuleCard key={c.id} capsule={c} />
                        ))
                      )}
                    </>
                  )}

                  {/* LATEST */}
                  {feedTab === 'latest' && (
                    <>
                      {filteredLatest.length === 0 ? (
                        <EmptyState
                          title="Nothing yet"
                          body="Be the first to seal a capsule."
                        />
                      ) : (
                        filteredLatest.map(c => (
                          <CapsuleCard key={c.id} capsule={c} />
                        ))
                      )}
                    </>
                  )}

                  {/* INCUBATING */}
                  {feedTab === 'incubating' && (
                    <>
                      {incubatingPosts.length === 0 ? (
                        <EmptyState
                          title="Nothing in incubation"
                          body="Incubating capsules appear here for 30 minutes before publishing."
                        />
                      ) : (
                        incubatingPosts.map(c => (
                          <PendingCapsuleCard
                            key={c.id}
                            capsule={c}
                            publicView={c.author_id !== user.id}
                            onWithdraw={c.author_id === user.id ? handleWithdraw : undefined}
                            onOpenWorkspace={c.author_id === user.id ? () => navigate(`/workspace/${c.id}`) : undefined}
                            onOpenThread={c.author_id !== user.id ? () => navigate(`/capsule/${c.id}`) : undefined}
                          />
                        ))
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}

          {/* ── CIVIC INTEL ── */}
          {mainTab === 'civic' && (
            <>
              <div className="debates-banner civic-banner">
                ◎ &nbsp;Local government decisions, translated. AI-summarized events from city council, planning commissions, police oversight, school boards, and county government.
              </div>

              {/* Filter bar */}
              <div className="civic-filters">
                <div className="civic-filter-group">
                  <span className="civic-filter-label">City</span>
                  <select
                    className="civic-filter-select"
                    value={civicCityFilter}
                    onChange={e => setCivicCityFilter(e.target.value)}
                  >
                    <option value="All">All cities</option>
                    {civicCities.map(c => (
                      <option key={c.city} value={c.city}>
                        {c.city}{c.state ? `, ${c.state}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="civic-filter-group">
                  <span className="civic-filter-label">Topic</span>
                  <select
                    className="civic-filter-select"
                    value={civicTopicFilter}
                    onChange={e => setCivicTopicFilter(e.target.value)}
                  >
                    <option value="All">All topics</option>
                    <option value="zoning">Zoning</option>
                    <option value="schools">Schools</option>
                    <option value="budget">Budget</option>
                    <option value="police">Public Safety</option>
                    <option value="environment">Environment</option>
                  </select>
                </div>
              </div>

              {civicLoading ? (
                <div className="feed-loading"><span className="spinner" /></div>
              ) : civicEvents.length === 0 ? (
                <EmptyState
                  title="No civic events yet"
                  body="Civic events will appear here once government documents are processed. An admin can seed content from the admin panel."
                />
              ) : (
                civicEvents.map(event => (
                  <CivicEventCard
                    key={event.id}
                    event={event}
                    onDebate={openDebateFromCivic}
                  />
                ))
              )}
            </>
          )}

          {/* ── BIG DEBATES ── */}
          {mainTab === 'debates' && (
            <>
              <div className="debates-banner">
                ◎ &nbsp;The most debated questions across philosophy, economics, politics, and culture. Pick one and seal a capsule.
              </div>
              {profile && (
                <div className="debates-depth-banner">
                  <div className="debates-depth-left">
                    <span className="debates-depth-label">Your depth score</span>
                    <div className="debates-depth-bar-wrap">
                      <div className="debates-depth-bar" style={{ width: `${Math.min(profile.depth_score || 0, 100)}%` }} />
                    </div>
                  </div>
                  <span className="debates-depth-number">⬡ {Math.round(profile.depth_score || 0)}</span>
                  <span className="debates-depth-tier">{
                    (profile.depth_score || 0) >= 80 ? 'High Depth' :
                    (profile.depth_score || 0) >= 50 ? 'Mid Depth' :
                    (profile.depth_score || 0) >= 20 ? 'Growing' : 'New'
                  }</span>
                </div>
              )}
              {DEBATE_TOPICS.map(section => (
                <div key={section.domain} className="debates-section">
                  <div className="debates-domain-header">
                    <span className="debates-domain-icon">{section.icon}</span>
                    <span className="debates-domain-label">{section.domain}</span>
                  </div>
                  <div className="debates-grid">
                    {section.topics.map(topic => (
                      <DebateCard key={topic.id} topic={topic} onDiscuss={openDiscussFromDebate} />
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* ── WITHDRAW MODAL ── */}
      {withdrawTarget && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setWithdrawTarget(null)}>
          <div className="modal">
            <div className="modal-title">Withdraw this capsule?</div>
            <div className="modal-body">
              This capsule is still in incubation and has not been published. Withdrawing it will permanently delete it. This action cannot be undone.
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setWithdrawTarget(null)}>Keep it</button>
              <button className="btn btn-danger" onClick={confirmWithdraw}>Withdraw</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      <div className={`toast ${toast.visible ? 'show' : ''} ${toast.type}`}>
        {toast.message}
      </div>
    </div>
  )
}

// ── Debate Card ───────────────────────────────────────────────────────────────

function DebateCard({ topic, onDiscuss }) {
  return (
    <div className="debate-card animate-slideUp">
      <div className="debate-title">{topic.title}</div>
      <div className="debate-question">{topic.question}</div>
      <div className="debate-footer">
        <button className="btn btn-primary debate-discuss-btn" onClick={() => onDiscuss(topic)}>
          Discuss →
        </button>
      </div>
    </div>
  )
}

// ── Empty state ───────────────────────────────────────────────────────────────

function EmptyState({ title, body }) {
  return (
    <div className="empty-state">
      <div className="empty-state-glyph">◇</div>
      <div className="empty-state-title">{title}</div>
      <div className="empty-state-body">{body}</div>
    </div>
  )
}
